import React from 'react'

import { Box, Stack } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { LogIn, ServerOff, WifiOff } from 'lucide-react'
import { Button as UIButton } from '@repo/ui'

import { AuthErrorMessage } from 'components/auth'
import { ROUTES } from 'routes/routes'

interface CertificatesLoadErrorProps {
  message?: string;
  /**
   * HTTP status of the failure, when there was one. 401/403 get a different
   * ACTION, not just different copy — see below. `undefined` is meaningful: it
   * is the no-response case (see `kind` below), not "unknown".
   */
  status?: number;
  onRetry: () => void;
}

/**
 * The three failure shapes this screen can be in. They come straight from
 * `useGetCertificates`, which is the one place that classifies them:
 *
 *   - `offline`  — no HTTP response at all (`status` undefined). The request
 *                  never reached a server, so this IS about the connection.
 *   - `auth`     — 401/403. Not retryable; the resolving action is signing in.
 *   - `server`   — any other non-2xx. A response came back, so the network is
 *                  demonstrably fine and the copy must not say otherwise.
 *
 * This distinction is the entire point of the screen. `apps/app` is reached by
 * tapping an NFC tag with a phone, in a shop or a warehouse, on cellular — so
 * network failure is the common path here, not the edge case, and more people
 * see this surface than most flows in the app. Wording a server fault as a
 * connection problem sends someone to restart their wifi over something they
 * cannot affect.
 */
type FailureKind = 'offline' | 'auth' | 'server'

const failureKind = (status?: number): FailureKind => {
  if (typeof status !== 'number') return 'offline'
  if (status === 401 || status === 403) return 'auth'
  return 'server'
}

/**
 * Neutral, informative line art — it should read as "something went wrong
 * here", not as decoration and not as an alarm. The glyph names WHICH failure
 * without repeating the sentence below it, so the icon and the message are
 * doing different jobs rather than the same one twice.
 *
 * Deliberately the same 56px circle treatment as `EmptyCertificates`: the two
 * occupy the same slot on the same screen, and a user who sees both should read
 * them as siblings rather than as two unrelated designs. `strokeWidth={1}` is
 * the repo's line-art weight and is what keeps "something failed" from reading
 * as "something is alarming".
 */
const KIND_ICON: Record<FailureKind, typeof WifiOff> = {
  offline: WifiOff,
  auth: LogIn,
  server: ServerOff,
}

export const CertificatesLoadError = ({ message, status, onRetry }: CertificatesLoadErrorProps) => {
  const navigate = useNavigate()
  /*
   * A 401/403 is not retryable, and offering "Try again" for one is worse than
   * offering nothing: the request goes out with the same (missing or expired)
   * credentials and comes back 403 forever, so the only control on the screen is
   * one that cannot work. Observed live — no `accessToken` cookie at all, so
   * every retry re-issued an unauthenticated GET.
   *
   * The action that actually resolves it is signing in, so that is the button.
   */
  const kind = failureKind(status)
  const isAuthFailure = kind === 'auth'
  const Icon = KIND_ICON[kind]

  return (
    <Stack
      p='0 16px'
      spacing={2}
      sx={{ textAlign: 'center', flex: 1, justifyContent: 'center', alignItems: 'center', minHeight: '276px' }}
    >
      <Box
        // The message is already announced by the alert's `role="alert"`; the
        // glyph repeating it would make a screen reader say the failure twice.
        aria-hidden
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: '#f2f2f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 0 0 1px #e6e6e6',
        }}
      >
        <Icon size={24} strokeWidth={1} color='#000' />
      </Box>

      <AuthErrorMessage
        message={message || 'We could not load your certificates right now. Please try again.'}
        /*
         * `backgroundColor: transparent` — the tinted fill is dropped HERE rather
         * than in `AuthErrorMessage` itself, which is shared with the auth screens
         * (sign in, claim) where the filled treatment still applies. Scoping it to
         * the call site keeps the blast radius at this one surface.
         *
         * At 6% alpha the fill was very nearly neutral anyway — measured
         * `rgba(220, 38, 38, 0.06)`, which composites over white to roughly
         * #FDF3F3 and reads as a grey box rather than as a red one. So it was
         * paying the visual cost of a filled block without delivering the status
         * colour that would have justified it. The border, icon and text still
         * carry the status, so this stays an alert; it just stops sitting on a
         * slab now that there is a placeholder above it doing the shaping.
         */
        sx={{ maxWidth: 360, mb: 0, backgroundColor: 'transparent' }}
      />
      <UIButton
        variant='secondary'
        onClick={isAuthFailure ? () => navigate(ROUTES.SIGN_IN) : onRetry}
      >
        {isAuthFailure ? 'Sign in' : 'Try again'}
      </UIButton>
    </Stack>
  )
}
